// Project space service — manages project CRUD and conversation binding via REST API
import { apiFetch } from './apiClient';

export interface Project {
  id: string;
  userId: string;
  name: string;
  desc: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectConversation {
  id: string;
  title: string;
  messageCount: number;
  lastActiveAt: string;
  status: string;
}

export async function listProjects(): Promise<Project[]> {
  const res = await apiFetch('/api/projects');
  if (!res.ok) throw new Error(`Failed to fetch projects: ${res.status}`);
  const data = await res.json();
  return data.projects || [];
}

export async function createProject(name: string, desc = '', color?: string): Promise<Project> {
  const res = await apiFetch('/api/projects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, desc, color }),
  });
  if (!res.ok) throw new Error(`Failed to create project: ${res.status}`);
  const data = await res.json();
  return data.project;
}

export async function updateProject(id: string, patch: Partial<Pick<Project, 'name' | 'desc' | 'color'>>): Promise<Project> {
  const res = await apiFetch(`/api/projects/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Failed to update project: ${res.status}`);
  const data = await res.json();
  return data.project;
}

export async function deleteProject(id: string): Promise<void> {
  const res = await apiFetch(`/api/projects/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to delete project: ${res.status}`);
}

export async function bindConversationToProject(conversationId: string, projectId: string | null): Promise<void> {
  const res = await apiFetch(`/api/conversations/${conversationId}/project`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId }),
  });
  if (!res.ok) throw new Error(`Failed to bind conversation: ${res.status}`);
}

export async function listProjectConversations(projectId: string): Promise<ProjectConversation[]> {
  const res = await apiFetch(`/api/projects/${projectId}/conversations`);
  if (!res.ok) throw new Error(`Failed to fetch project conversations: ${res.status}`);
  const data = await res.json();
  return data.conversations || [];
}

export interface ProjectPlan {
  id: string;
  title: string;
  description?: string;
  status: string;
  steps: Array<{ id: string; title: string; status: string }>;
  updatedAt: string;
}

export async function listProjectPlans(): Promise<ProjectPlan[]> {
  const res = await apiFetch('/api/plans?source=xiaofeng&limit=5');
  if (!res.ok) throw new Error(`Failed to fetch plans: ${res.status}`);
  const data = await res.json();
  return data.plans || [];
}

export async function createPlan(
  title: string,
  opts: { description?: string; priority?: string; steps?: Array<{ id: string; title: string; status: string }>; tags?: string[] } = {},
): Promise<ProjectPlan> {
  const res = await apiFetch('/api/plans', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title,
      description: opts.description || '',
      priority: opts.priority || 'medium',
      steps: opts.steps || [],
      tags: opts.tags || [],
      source: 'user',
    }),
  });
  if (!res.ok) throw new Error(`Failed to create plan: ${res.status}`);
  const data = await res.json();
  return data.plan;
}

export async function updatePlan(id: string, patch: Partial<ProjectPlan>): Promise<ProjectPlan> {
  const res = await apiFetch(`/api/plans/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Failed to update plan: ${res.status}`);
  const data = await res.json();
  return data.plan;
}

export async function deletePlan(id: string): Promise<void> {
  const res = await apiFetch(`/api/plans/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to delete plan: ${res.status}`);
}

export async function getMemoryCount(): Promise<number> {
  const res = await apiFetch('/api/memories?limit=1000');
  if (!res.ok) return 0;
  const data = await res.json();
  return Array.isArray(data) ? data.length : 0;
}

export interface ProjectWorkProduct {
  id: string;
  task: string;
  deliverableType: string;
  finalOutput: string;
  acceptanceCriteriaCount: number;
  createdAt: string;
}

export async function listWorkProducts(): Promise<ProjectWorkProduct[]> {
  const res = await apiFetch('/api/work-products');
  if (!res.ok) throw new Error(`Failed to fetch work products: ${res.status}`);
  const data = await res.json();
  return data.workProducts || [];
}
